import axios from "axios";
import { buildApiUrl, getApiCallerRole, isSupabaseFunctionsApi } from "../config/api";
export function normalizePaymentSession(data) {
    if (!data || typeof data !== "object") return null;

    const referenceNumber = data.referenceNumber || data.txRef;
    const publicKey =
        data.publicKey ||
        String(process.env.REACT_APP_PAYCHANGU_PUBLIC_KEY || "").trim() ||
        null;

    if (!referenceNumber || !publicKey) {
        return null;
    }

    return {
        ...data,
        referenceNumber,
        txRef: data.txRef || referenceNumber,
        publicKey,
    };
}

export function paymentSessionErrorFromResponse(data) {
    if (!data || typeof data !== "object") {
        return "PayChangu session was not returned by the payment service.";
    }

    if (data.message && !data.publicKey && !data.referenceNumber) {
        const msg = String(data.message);
        if (/email and password are required/i.test(msg)) {
            return (
                "Subscription payment is not enabled on the server yet. In Supabase, delete unused Edge " +
                "Functions if needed, redeploy AdminReg, and set PAYCHANGU_PUBLIC_KEY + PAYCHANGU_SECRET_KEY secrets."
            );
        }
        if (isStaleAdminRegConflictMessage(msg)) {
            return (
                "Your school account was created, but Supabase AdminReg is still on the old version and " +
                "blocked payment (it tried to register you again). Sign in with the email and password you " +
                "just used, then pay from the dashboard — or redeploy AdminReg with the latest code and PayChangu secrets."
            );
        }
        return msg;
    }

    if ((data.role === "Admin" || data._id) && !data.publicKey) {
        return (
            "The payment server is out of date and returned a school record instead of a PayChangu session. " +
            "In Supabase Dashboard, redeploy the AdminReg Edge Function, set secrets PAYCHANGU_PUBLIC_KEY and " +
            "PAYCHANGU_SECRET_KEY, then try again."
        );
    }

    if (!data.publicKey && !process.env.REACT_APP_PAYCHANGU_PUBLIC_KEY) {
        return (
            "PayChangu public key is missing. Add PAYCHANGU_PUBLIC_KEY to Supabase Edge Function secrets " +
            "(or REACT_APP_PAYCHANGU_PUBLIC_KEY on Netlify), then redeploy AdminReg."
        );
    }

    return "PayChangu session was not returned by the payment service.";
}

function isStaleAdminRegConflictMessage(message) {
    const text = String(message || "").toLowerCase();
    return text.includes("email already exists") || text.includes("school name already exists");
}

/** Payment-only body — must not include registration fields (old AdminReg treats them as a second signup). */
export function buildPaymentCheckoutPayload({
    adminId,
    planId,
    amount,
    returnOrigin,
    schoolName,
    adminEmail,
    adminName,
}) {
    return {
        intent: "subscription-payment",
        adminId,
        planId,
        amount,
        returnOrigin,
        schoolName,
        adminEmail,
        adminName,
    };
}

/** @deprecated Use buildPaymentCheckoutPayload — kept for callers that pass a full fields object. */
export function buildPaymentCheckoutBody(fields) {
    return buildPaymentCheckoutPayload({
        adminId: fields.adminId,
        planId: fields.planId,
        amount: fields.amount,
        returnOrigin: fields.returnOrigin,
        schoolName: fields.schoolName,
        adminEmail: fields.adminEmail,
        adminName: fields.adminName,
    });
}

/** POST body to poll payment status. */
export function buildPaymentStatusBody(referenceNumber) {
    return {
        intent: "subscription-payment-status",
        referenceNumber,
    };
}

export function buildPaymentConfirmBody(referenceNumber) {
    return {
        intent: "subscription-payment-confirm",
        referenceNumber,
    };
}

export function buildPaymentAbandonBody(referenceNumber) {
    return {
        intent: "subscription-payment-abandon",
        referenceNumber,
    };
}

function supabaseGatewayParams() {
    const role = getApiCallerRole();
    return role ? { callerRole: role } : {};
}

/** Start PayChangu checkout via AdminReg (intent in POST body). */
export async function initiateSubscriptionPaymentCheckout(fields) {
    const payload = buildPaymentCheckoutPayload({
        adminId: fields.adminId,
        planId: fields.planId,
        amount: fields.amount,
        returnOrigin: fields.returnOrigin,
        schoolName: fields.schoolName,
        adminEmail: fields.adminEmail,
        adminName: fields.adminName,
    });
    const params = supabaseGatewayParams();

    if (!isSupabaseFunctionsApi()) {
        return axios.post(buildApiUrl("/SubscriptionPaymentCheckout"), payload, { params });
    }

    const paymentUrl = `${buildApiUrl("/AdminReg")}?action=subscription-payment`;

    try {
        const response = await axios.post(paymentUrl, payload, { params });
        const session = normalizePaymentSession(response.data);
        if (session) return response;

        if ((response.data?.role === "Admin" || response.data?._id) && !response.data?.publicKey) {
            throw new Error(paymentSessionErrorFromResponse(response.data));
        }

        if (response.data?.message && !response.data?.publicKey) {
            throw new Error(paymentSessionErrorFromResponse(response.data));
        }

        if (response.data?.error && !response.data?.publicKey) {
            throw new Error(String(response.data.error));
        }

        return response;
    } catch (error) {
        const msg = String(
            error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                "",
        );
        if (/email and password are required/i.test(msg)) {
            throw new Error(paymentSessionErrorFromResponse({ message: msg }));
        }
        if (isStaleAdminRegConflictMessage(msg)) {
            const err = new Error(paymentSessionErrorFromResponse({ message: msg }));
            err.isStaleAdminRegPayment = true;
            throw err;
        }
        throw error;
    }
}

/** Poll checkout status — Supabase AdminReg/Login (POST) or local Express (GET). */
export async function fetchSubscriptionPaymentStatus(referenceNumber) {
    const ref = String(referenceNumber || "").trim();
    if (!ref) {
        throw new Error("referenceNumber is required");
    }

    if (isSupabaseFunctionsApi()) {
        const payload = buildPaymentStatusBody(ref);
        const params = supabaseGatewayParams();
        const statusUrl = `${buildApiUrl("/AdminReg")}?action=subscription-payment-status`;
        return axios.post(statusUrl, payload, { params });
    }

    return axios.get(buildApiUrl(`/SubscriptionPaymentInitiate/${encodeURIComponent(ref)}`));
}

/** Mark PayChangu popup as opened — payment appears in operator lists only after this. */
export async function confirmSubscriptionPaymentCheckout(referenceNumber) {
    const ref = String(referenceNumber || "").trim();
    if (!ref) return null;

    if (!isSupabaseFunctionsApi()) {
        return null;
    }

    const payload = buildPaymentConfirmBody(ref);
    const params = supabaseGatewayParams();
    const url = `${buildApiUrl("/AdminReg")}?action=subscription-payment-confirm`;
    return axios.post(url, payload, { params });
}

/** User left checkout without paying — cancel prepared/pending session. */
export async function abandonSubscriptionPaymentCheckout(referenceNumber) {
    const ref = String(referenceNumber || "").trim();
    if (!ref) return null;

    if (!isSupabaseFunctionsApi()) {
        return null;
    }

    const payload = buildPaymentAbandonBody(ref);
    const params = supabaseGatewayParams();
    const url = `${buildApiUrl("/AdminReg")}?action=subscription-payment-abandon`;
    return axios.post(url, payload, { params });
}
